import { LNotification } from "@lib/abpUtility"
import { notifySuccess } from "@lib/helper"

import http from "@services/httpService"
import { PagedResultDto } from "@services/dto/pagedResultDto"
import { downloadFile } from "@lib/helperFile"
import dayjs from "dayjs"

// Req1.5: tên file xuất gồm loại danh sách + kỳ + timestamp
// (vd DotThongBaoPhi_072026_20260719_1530.xlsx)
const buildExportFileName = (prefix: string, period?: string): string => {
  const periodPart = period ? period.replace(/\//g, "") : ""
  const timestamp = dayjs().format("YYYYMMDD_HHmm")
  return `${[prefix, periodPart, timestamp].filter(Boolean).join("_")}.xlsx`
}

/**
 * 202 Accepted — "đã nhận việc, đang xử lý nền". `ExportContractNotificationDossier` dùng mã này
 * cho gói lớn (Req 15.6) nên FE PHẢI phân biệt nó với 200-có-blob.
 */
const HTTP_STATUS_ACCEPTED = 202

class FeeNotificationService {
  public async getAll(params: any): Promise<PagedResultDto<any>> {
    const res = await http.get("api/services/app/FeeNotification/GetList", {
      params,
    })
    const result = res.data.result
    return result
  }

  public async get(params: any): Promise<any> {
    const res = await http.get("api/services/app/FeeNotification/Get", {
      params,
    })
    const result = res.data.result
    return result
  }
  public async getAllContractFeeNotification(
    params: any
  ): Promise<PagedResultDto<any>> {
    const res = await http.get(
      "api/services/app/FeeNotification/GetAllContractFeeNotification",
      { params }
    )
    const result = res.data.result
    return result
  }
  public async getAllContractInquiryFeeNotification(
    params: any
  ): Promise<PagedResultDto<any>> {
    const res = await http.get(
      "api/services/app/FeeNotification/GetAllContractInquiryFeeNotification",
      { params }
    )
    const result = res.data.result
    return result
  }

  public async getFeeNotificationHistory(
    params: any
  ): Promise<PagedResultDto<any>> {
    const res = await http.get(
      "api/services/app/FeeNotification/GetFeeNotificationHistory",
      { params }
    )
    const result = res.data.result

    return result
  }

  // Req3: lịch sử thông báo phí theo hợp đồng (panel truy vết ở màn chi tiết hợp đồng).
  // fee-notification-reporting Req 5.2: `onlySent` là cờ TUỲ CHỌN của BE, mặc định `false` = giữ
  // nguyên hành vi cũ (trả cả đợt chưa từng gửi). Bỏ trống ⇒ UI tự phân vùng theo `isSendAttempted`.
  public async getContractNotificationHistory(
    contractId: any,
    onlySent?: boolean
  ): Promise<any[]> {
    const res = await http.get(
      "api/services/app/FeeNotification/GetContractNotificationHistory",
      { params: { contractId, onlySent } }
    )
    return res.data.result
  }

  // Req4: thống kê gửi cấp đợt (Delivery Dashboard).
  public async getBatchDeliveryStats(batchId: any): Promise<any> {
    const res = await http.get(
      "api/services/app/FeeNotification/GetBatchDeliveryStats",
      { params: { batchId } }
    )
    return res.data.result
  }

  public async create(body: any) {
    const res = await http.post("api/services/app/FeeNotification/Create", body)
    notifySuccess(
      LNotification("SUCCESS"),
      LNotification("SAVING_SUCCESSFULLY")
    )

    return res.data.result
  }

  // Get detail fee
  public async getContractFeeDetail(params: {
    feeNotificationId: any
    contractId: any
  }): Promise<any> {
    const res = await http.get(
      "api/services/app/FeeNotification/GetContractFeeDetail",
      { params }
    )
    const result = res.data.result

    return result
  }
  //Get review fee
  // Lọc theo LÔ THU dùng `transactionCode` (BE đã bỏ param `receiptId`). Bỏ trống với đợt nhắc nợ —
  // đợt đó không gắn lô thu nào.
  public async getFeeNotificationSendDetail(params: {
    feeNotificationId: any
    contractId: any
    channel: "EMAIL" | "SMS"
    transactionCode?: string
  }): Promise<any> {
    const res = await http.get(
      "api/services/app/FeeNotification/GetFeeNotificationSendDetail",
      { params }
    )
    const result = res.data.result

    return result
  }

  //Send notification
  public async sendAllFeeNotification(body: any) {
    const res = await http.post(
      "api/services/app/FeeNotification/SendAllFeeNotification",
      body
    )
    notifySuccess(LNotification("SUCCESS"), LNotification("SEND_SUCCESSFULLY"))
    return res.data.result
  }

  // Gửi chỉ những hợp đồng người dùng đã chọn trong chi tiết đợt (BE dùng cùng guard với gửi cả đợt).
  public async sendSelectedFeeNotification(body: {
    feeNotificationId: number
    feeNotificationContractIds: number[]
  }) {
    const res = await http.post(
      "api/services/app/FeeNotification/SendSelectedFeeNotification",
      body
    )
    notifySuccess(LNotification("SUCCESS"), LNotification("SEND_SUCCESSFULLY"))
    return res.data.result
  }

  public async reSendFeeNotificationWithCustomer(body: any) {
    const res = await http.post(
      "api/services/app/FeeNotification/ReSendFeeNotificationWithCustomer",
      body
    )
    notifySuccess(LNotification("SUCCESS"), LNotification("SEND_SUCCESSFULLY"))
    return res.data.result
  }


  /*
  * @param params
  * @returns
  * @example
  * const params = {
  *   FeeNotificationContractId: 1,
  * }
  */
  public async deleteContractFeeNotification(params: any) {
    const res = await http.delete(
      "/api/services/app/FeeNotification/DeleteDetail",
      { params }
    )
    notifySuccess(LNotification("SUCCESS"), LNotification("DELETE_SUCCESSFULLY"))
    return res.data.result
  }

  // Req1.1: xuất Excel danh sách đợt theo bộ lọc đang áp
  public async exportBatchList(params: any): Promise<void> {
    const res = await http.get("api/Export/ExportFeeNotificationBatchList", {
      params,
      responseType: "blob",
    })
    downloadFile(res.data, buildExportFileName("DotThongBaoPhi", params?.period))
  }

  // Req1.2: xuất Excel danh sách HĐ trong đợt (tab Info)
  public async exportBatchDetail(batchId: any, period?: string): Promise<void> {
    const res = await http.get("api/Export/ExportFeeNotificationBatchDetail", {
      params: { batchId },
      responseType: "blob",
    })
    downloadFile(
      res.data,
      buildExportFileName("ChiTietDotThongBaoPhi", period)
    )
  }

  // Req1.3: xuất Excel log gửi theo bộ lọc (tab Lịch sử gửi)
  public async exportSendHistory(params: any, period?: string): Promise<void> {
    const res = await http.get("api/Export/ExportFeeNotificationSendHistory", {
      params,
      responseType: "blob",
    })
    downloadFile(
      res.data,
      buildExportFileName("LichSuGuiThongBaoPhi", period)
    )
  }

  // Req2: tải hàng loạt PDF của đợt thông báo phí dưới dạng .zip.
  // Nếu backend build sẵn (IsProcessing = false) → tải blob ngay; nếu đẩy nền → báo "đang xử lý".
  public async buildBatchPdfZip(batchId: any): Promise<void> {
    const res = await http.post(
      "api/services/app/FeeNotification/BuildBatchPdfZip",
      { id: batchId }
    )
    const result = res.data.result
    if (result && !result.isProcessing && result.fileToken) {
      const fileRes = await http.get(
        "api/Export/ExportFeeNotificationPdfZip",
        {
          params: { fileToken: result.fileToken, fileName: result.fileName },
          responseType: "blob",
        }
      )
      downloadFile(fileRes.data, result.fileName)
    } else {
      notifySuccess(
        LNotification("SUCCESS"),
        LNotification("FEE_NOTIFICATION_PDF_ZIP_PROCESSING")
      )
    }
  }

  /**
   * Req 15.6 (fee-notification-reporting) — hồ sơ chứng minh đã thông báo của MỘT hợp đồng (.zip).
   *
   * Endpoint có **hai kết cục** giống hệt zip theo đợt ở trên: gói nhỏ được build ngay trong
   * request và trả blob; gói lớn được đẩy sang `BuildFeeNotificationPdfZipJob` nên controller đáp
   * **202 Accepted** kèm DTO (`isProcessing = true`) và KHÔNG có blob nào để tải — lúc đó chỉ báo
   * "đang xử lý", link tải tới qua in-app notification khi job xong. Phân biệt hai nhánh bằng
   * `res.status` (thân 202 là JSON gói trong Blob nên không thể đem đi `downloadFile`).
   */
  public async exportContractNotificationDossier(
    contractId: any
  ): Promise<void> {
    const res = await http.get(
      "api/Export/ExportContractNotificationDossier",
      { params: { contractId }, responseType: "blob" }
    )
    if (res.status === HTTP_STATUS_ACCEPTED) {
      notifySuccess(
        LNotification("SUCCESS"),
        LNotification("FEE_NOTIFICATION_PDF_ZIP_PROCESSING")
      )
      return
    }
    const timestamp = dayjs().format("YYYYMMDD_HHmm")
    downloadFile(res.data, `HOSOTHONGBAOPHI_${contractId}_${timestamp}.zip`)
  }
}

export default new FeeNotificationService()
